import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AdminLayout } from '@/layouts/AdminLayout';
import { AuthGuard } from './AuthGuard';
import { RoleGuard } from './RoleGuard';

// Lazy-loaded pages
const DashboardPage = lazy(() => import('@/pages/Dashboard'));
const PackagesPage = lazy(() => import('@/pages/Packages'));
const PackageFormPage = lazy(() => import('@/pages/PackageForm'));
const PackageTracksPage = lazy(() => import('@/pages/PackageTracks'));
const UsersPage = lazy(() => import('@/pages/Users'));
const UserDetailPage = lazy(() => import('@/pages/UserDetail'));
const FinancePage = lazy(() => import('@/pages/Finance'));
const AnalyticsPage = lazy(() => import('@/pages/Analytics'));
const NotificationsPage = lazy(() => import('@/pages/Notifications'));
const BannersPage = lazy(() => import('@/pages/Banners'));
const AuditPage = lazy(() => import('@/pages/Audit'));
const SettingsPage = lazy(() => import('@/pages/Settings'));
const LoginPage = lazy(() => import('@/pages/Login'));

export function AppRouter() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />

      {/* Protected admin layout — requires authentication */}
      <Route
        element={
          <AuthGuard>
            <AdminLayout />
          </AuthGuard>
        }
      >
        {/* Dashboard — accessible by all roles */}
        <Route
          index
          element={
            <RoleGuard route="dashboard">
              <DashboardPage />
            </RoleGuard>
          }
        />

        {/* Content */}
        <Route
          path="packages"
          element={
            <RoleGuard route="packages">
              <PackagesPage />
            </RoleGuard>
          }
        />
        <Route
          path="packages/new"
          element={
            <RoleGuard route="packages">
              <PackageFormPage />
            </RoleGuard>
          }
        />
        <Route
          path="packages/:id/edit"
          element={
            <RoleGuard route="packages">
              <PackageFormPage />
            </RoleGuard>
          }
        />
        <Route
          path="packages/:id/tracks"
          element={
            <RoleGuard route="packages">
              <PackageTracksPage />
            </RoleGuard>
          }
        />

        {/* Users */}
        <Route
          path="users"
          element={
            <RoleGuard route="users">
              <UsersPage />
            </RoleGuard>
          }
        />
        <Route
          path="users/:id"
          element={
            <RoleGuard route="users">
              <UserDetailPage />
            </RoleGuard>
          }
        />

        {/* Finance */}
        <Route
          path="finance"
          element={
            <RoleGuard route="finance">
              <FinancePage />
            </RoleGuard>
          }
        />

        {/* Analytics */}
        <Route
          path="analytics"
          element={
            <RoleGuard route="analytics">
              <AnalyticsPage />
            </RoleGuard>
          }
        />

        {/* Notifications */}
        <Route
          path="notifications"
          element={
            <RoleGuard route="notifications">
              <NotificationsPage />
            </RoleGuard>
          }
        />

        {/* Banners */}
        <Route
          path="banners"
          element={
            <RoleGuard route="banners">
              <BannersPage />
            </RoleGuard>
          }
        />

        {/* Audit */}
        <Route
          path="audit"
          element={
            <RoleGuard route="audit">
              <AuditPage />
            </RoleGuard>
          }
        />

        {/* Settings */}
        <Route
          path="settings"
          element={
            <RoleGuard route="settings">
              <SettingsPage />
            </RoleGuard>
          }
        />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
